import { faEllipsisV, faUserPlus } from '@fortawesome/pro-regular-svg-icons';
// eslint-disable-next-line no-restricted-imports
import ListItemText from '@mui/material/ListItemText';
// eslint-disable-next-line no-restricted-imports
import MenuItem from '@mui/material/MenuItem';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from 'src/components/ConfirmDialog';
import { Drawer } from 'src/components/Drawer';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Link } from 'src/components/Link';
import { Scroller } from 'src/components/Scroller';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { Appointment } from 'src/models/appointment/_types';
import { getAppointmentType } from 'src/models/appointment/utils';
import { isTrainer } from 'src/models/user/utils';
import { MessageDialog } from 'src/pages/Clients/ClientsListPage/BatchActions/Dialogs/MessageDialog';
import { usePermissionCheck } from 'src/shared/usePermissionCheck';

import { ClientSessionDrawer } from '../ClientSessionDrawer';
import { EditAppointmentDialog } from '../EditAppointmentDialog';

import { AddUsersDialog } from './AddUsersDialog';
import { ClientAppointmentDetails } from './AppointmentDetailsClient';
import { TrainerAppointmentDetails } from './AppointmentDetailsTrainer';
import { AppointmentDrawerActions } from './AppointmentDrawerActions';
import { useAppointmentDrawer } from './useAppointmentDrawer';

import type { AccountManager, User } from 'src/models/user/_types';

interface Props {
  appointment: Appointment | null | undefined;
  onClose?: () => void;
  dependents?: AccountManager[];
}

export const AppointmentDrawer: React.FC<Props> = ({
  appointment,
  onClose,
  dependents = [],
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'admin.platform.appointment.drawer.appointmentDrawer',
  });
  const { user } = useSession();
  const { canCancelVisit } = usePermissionCheck();
  const {
    visit,
    setVisitId,
    showAppointmentDialog,
    setShowAppointmentDialog,
    openDialog,
    setOpenDialog,
    handleAppointmentCancel,
    handleAppointmentDelete,
    isAppointmentFuture,
    addUserOpen,
    setAddUserOpen,
    addUserTab,
    setAddUserTab,
    messageAttendees,
    setMessageAttendees,
    attendeeVisits,
  } = useAppointmentDrawer({ appointment, onClose });

  return (
    <>
      <Drawer
        open={!!appointment}
        onClose={onClose}
        title={appointment ? getAppointmentType(appointment) : ''}
        rightAdornment={
          isTrainer(user) &&
          appointment && (
            <Stack direction="row" spacing={1}>
              <IconButton
                onClick={() => {
                  setAddUserOpen(true);
                }}
              >
                <Icon size="small" icon={faUserPlus} />
              </IconButton>
              <DropdownMenu
                trigger={
                  <IconButton>
                    <Icon size="small" icon={faEllipsisV} />
                  </IconButton>
                }
              >
                <Stack spacing={1}>
                  <MenuItem
                    onClick={() => {
                      setShowAppointmentDialog(true);
                    }}
                  >
                    <ListItemText>{t('editSession')}</ListItemText>
                  </MenuItem>
                  {canCancelVisit && (
                    <>
                      <MenuItem
                        onClick={() => {
                          setOpenDialog('confirm-cancel');
                        }}
                      >
                        <ListItemText>{t('cancelSession')}</ListItemText>
                      </MenuItem>
                      {!appointment.scheduledAppointmentId && (
                        <MenuItem
                          onClick={() => {
                            setOpenDialog('confirm-delete');
                          }}
                        >
                          <ListItemText>{t('deleteSession')}</ListItemText>
                        </MenuItem>
                      )}
                    </>
                  )}
                  <MenuItem
                    component={Link}
                    href={`/fbm/services/${appointment.serviceId}`}
                    target="_blank"
                  >
                    <ListItemText>
                      {t('editGlobalServiceSettings')}
                    </ListItemText>
                  </MenuItem>
                  {attendeeVisits.length > 0 && (
                    <MenuItem
                      onClick={() => {
                        setMessageAttendees(
                          attendeeVisits.map((v) => v.user) as User[]
                        );
                      }}
                    >
                      <ListItemText>{t('messageAllAttendees')}</ListItemText>
                    </MenuItem>
                  )}
                </Stack>
              </DropdownMenu>
            </Stack>
          )
        }
      >
        {appointment && (
          <>
            <Scroller direction="column">
              {isTrainer(user) && (
                <TrainerAppointmentDetails
                  appointment={appointment}
                  setVisit={(visit) => setVisitId(visit.id)}
                  isAppointmentFuture={isAppointmentFuture(appointment)}
                  handleEditDetailsClick={() => setShowAppointmentDialog(true)}
                />
              )}
              {!isTrainer(user) && (
                <ClientAppointmentDetails
                  appointment={appointment}
                  visit={visit}
                />
              )}
            </Scroller>
            <AppointmentDrawerActions
              appointment={appointment}
              dependents={dependents}
            />
          </>
        )}
      </Drawer>
      {appointment && visit && (
        <ClientSessionDrawer
          onClose={() => setVisitId(null)}
          appointment={appointment}
          visit={visit}
        />
      )}
      {appointment && (
        <>
          <EditAppointmentDialog
            appointment={appointment}
            open={showAppointmentDialog}
            onClose={() => setShowAppointmentDialog(false)}
          />
          <AddUsersDialog
            appointment={appointment}
            open={addUserOpen}
            onClose={() => {
              setAddUserOpen(false);
            }}
            handleTabClick={(tab) => {
              setAddUserTab(tab);
            }}
            activeTab={addUserTab}
          />
        </>
      )}
      <ConfirmDialog
        open={openDialog == 'confirm-cancel'}
        title={t('confirmCancelTitle')}
        actions={[
          {
            label: t('confirmCancelButton'),
            color: 'error',
            variant: 'prominent',
            onClick: () => handleAppointmentCancel(),
          },
          {
            label: t('goBack'),
            onClick: () => setOpenDialog(''),
            color: 'neutral',
            variant: 'borderless',
          },
        ]}
      >
        {t('confirmCancelMessage')}
      </ConfirmDialog>
      <ConfirmDialog
        open={openDialog == 'confirm-delete'}
        title={t('confirmDeleteTitle')}
        actions={[
          {
            label: t('confirmDeleteButton'),
            color: 'error',
            variant: 'prominent',
            onClick: () => handleAppointmentDelete(),
          },
          {
            label: t('goBack'),
            onClick: () => setOpenDialog(''),
            color: 'neutral',
            variant: 'borderless',
          },
        ]}
      >
        <Stack spacing={3}>
          <Typography>{t('confirmDeleteMessage')}</Typography>
          <Typography>{t('deleteSessionWarning')}</Typography>
        </Stack>
      </ConfirmDialog>
      {messageAttendees.length > 0 && (
        <MessageDialog
          clients={[]}
          users={messageAttendees}
          selectAll={false}
          onClose={() => setMessageAttendees([])}
        />
      )}
    </>
  );
};
